import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth, loginWithGoogle, loginWithMicrosoft } from '@/lib/auth'
import { apiPost, ApiError } from '@/lib/api'

export function Login() {
  const { isAuthenticated, isLoading } = useAuth()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devUrl, setDevUrl] = useState<string | null>(null)

  if (isAuthenticated && !isLoading) {
    return <Navigate to="/dashboard" replace />
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSending(true)
    try {
      const data = await apiPost<{ ok: boolean; devUrl?: string }>('/auth/magic-link', { email })
      setSent(true)
      if (data.devUrl) setDevUrl(data.devUrl)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Error al enviar el enlace. Intenta de nuevo.')
      }
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        {/* Header */}
        <h1 className="text-center text-2xl font-bold text-gray-900">
          1-Klick Sales
        </h1>
        <p className="mt-1 text-center text-sm text-gray-500">
          Iniciar sesion
        </p>

        {sent ? (
          /* Success state */
          <div className="mt-8 rounded-md bg-green-50 p-4 text-center">
            <p className="text-sm text-green-800">
              Revisa tu correo electronico. Te enviamos un enlace para iniciar
              sesion.
            </p>
            {devUrl && (
              <a
                href={devUrl}
                className="mt-3 inline-block rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                Dev: abrir magic link
              </a>
            )}
          </div>
        ) : (
          <>
            {/* OAuth section */}
            <div className="mt-8 space-y-3">
              <button
                type="button"
                onClick={loginWithGoogle}
                className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                  G
                </span>
                Continuar con Google
              </button>
              <button
                type="button"
                onClick={loginWithMicrosoft}
                className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                  M
                </span>
                Continuar con Microsoft
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">o</span>
              </div>
            </div>

            {/* Magic link section */}
            <form onSubmit={handleMagicLink} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Correo electronico
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {sending ? 'Enviando...' : 'Enviar enlace magico'}
              </button>
              {error && (
                <p className="text-center text-sm text-red-600">{error}</p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  )
}
